import { Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import QueryString from "qs";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { getSteps } from "../../../api/courses.api";
import { getArchives } from "../../../api/homepageManagementStudyArchive.api";
import { getSubjects } from "../../../api/subject.api";
import Button from "../../../components/Button";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from "../../../components/layout/SimpleHeader";
import Loader from "../../../components/Loader";
import Pagination from "../../../components/Pagination";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import { IGetSteps } from "../../../models/courses.model";
import { IArchiveItem, IGetArchives } from "../../../models/homepageManagementStudyArchive.model";
import { IGetSubjects } from "../../../models/subject.model";

const HomepageArchive = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { data: res, isLoading, error, execute } = useAxios<IGetArchives>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSteps>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetSubjects>();

  const [steps, setSteps] = useState<any>([]);
  const [courseCode, setCourseCode] = useState<number>();
  const [subjectCode, setSubjectCode] = useState('');
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.courseCode) {
      _param.queryCourseCode = Number(query.courseCode);
      setCourseCode(_param.queryCourseCode);
    }
    if (query.subjectCode) {
      _param.subjectCode = Number(query.subjectCode);
      setSubjectCode(_param.subjectCode);
    }
    if (query.title) {
      _param.title = String(query.title);
      setTitle(_param.title);
    }

    execute(getArchives(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  useEffect(() => {
    execute2(getSteps());
    execute3(getSubjects());
  }, []);

  useEffect(() => {
    if (res2 && !isLoading2) {
      const _steps: any = [];
      res2.data?.forEach((level) => {
        level.step?.forEach((step) => {
          _steps.push({
            label: step.courseName,
            value: step.courseCode,
          });
        });
      });
      setSteps(_steps);
    }
  }, [res2, isLoading2]);

  const onSearch = () => {
    const _param: any = { page: 1 };
    if (courseCode === 0 || courseCode) _param.courseCode = courseCode;
    if (subjectCode) _param.subjectCode = subjectCode;
    if (title) _param.title = title;

    history.push(`/homepage/archive?${QueryString.stringify(_param)}`);
  }

  const columns: TableColumnsType<IArchiveItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'archiveCode',
      key: 'archiveCode',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.archiveCode - b.archiveCode,
    },
    {
      title: t('homepage.archive.column2'),
      dataIndex: 'courseName',
      key: 'courseName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.courseName?.localeCompare(b.courseName),
    },
    {
      title: t('homepage.archive.column3'),
      dataIndex: 'subjectName',
      key: 'subjectName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.subjectName?.localeCompare(b.subjectName),
      render: (value, record) => record.subjectCode === 0 ? '-' : value,
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      sorter: (a, b) => a.title?.localeCompare(b.title),
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('homepage.archive.column6'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('homepage.archive.column7'),
      dataIndex: 'readCount',
      key: 'readCount',
      align: 'center',
      width: '7%',
      sorter: (a, b) => a.readCount - b.readCount,
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t("common.menu.homepage")}
      currentMenu={t('homepage.archive.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('homepage.archive.column2')}</label>
            <div>
              <Select
                placeholder={t('common.button.select')}
                value={courseCode}
                onChange={value => setCourseCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                <Select.Option value={0}>ALL</Select.Option>
                {steps?.map((item: any) => (
                  <Select.Option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('homepage.archive.column3')}</label>
            <div>
              <Select
                placeholder={t('homepage.archive.column3')}
                value={subjectCode}
                onChange={value => setSubjectCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {res3?.data?.map(item => (
                  <Select.Option
                    key={item.subjectCode}
                    value={item.subjectCode}
                  >
                    {item.subjectName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('common.column.title')}</label>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.archive.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/archive/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/homepage/archive/${record.archiveCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="archiveCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/homepage/archive"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/archive/create')}
              text={t('homepage.archive.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>        
      </div>
    </div>
    </>
  );
}

export default HomepageArchive;
